import { CloseOutlined, SaveOutlined } from "@ant-design/icons";
import { Button, Col, DatePicker, Drawer, Empty, Flex, Form, Input, InputNumber, notification, Row, Select, Spin } from "antd";
import { useLazyGetCategoriesBreedInByFarmQuery, useLazyGetFarmsForSalesOrderQuery } from "api";
import { useCreatePriceListMutation, useUpdatePriceListMutation } from "api/priceListApi";
import { useLazyGetCustomersQuery } from "api/sales-order/customer-api";
import { handleSelectFilter, translationStatusPriceList } from "commons/constants/util";
import dayjs from "dayjs";
import useLoading from "hooks/useLoading";
import useSelect from "hooks/useSelect";
import { GenderChicken } from "pages/sales-order/components/SalesOrderModalV2";
import { useEffect } from "react";
import { EnumCurrencyPriceList, EnumStatusPriceList, EnumUomPriceList, PriceListResponse } from "types/price-list.type";

interface PriceListModalProps {
	title: string;
	open: boolean;
	onClose: () => void;
	priceList?: PriceListResponse;
}

interface PriceListForm extends Pick<PriceListResponse, "priceListCode" | "customerCode" | "productCode" | "priceAmount" | "uom" | "chickenType" | "currency" | "gender" | "status"> {
	farmCode: string;
	validFrom: dayjs.Dayjs;
	validTo: dayjs.Dayjs;
}

export const PriceListModal = ({ priceList, title, open, onClose }: PriceListModalProps) => {
	const { isLoading: isLoadingQueries } = useLoading("queries");

	const { isLoading: isLoadingMutations } = useLoading("mutations");

	const [form] = Form.useForm<PriceListForm>();

	const [getCustomers, { data: dataGetCustomers }] = useLazyGetCustomersQuery();

	const { options: optionsCustomers } = useSelect<Customer>(getCustomers, dataGetCustomers);

	const [getFarmsForSalesOrder, { data: dataFarms }] = useLazyGetFarmsForSalesOrderQuery();

	const { options: optionsFarms, handleOnClear: handleOnClearFarms, isFetching: isFetchingGetFarms } = useSelect<FarmsReport>(getFarmsForSalesOrder, dataFarms);

	const [getCategoriesBreedInByFarm, { data: dataBreedInByFarm }] = useLazyGetCategoriesBreedInByFarmQuery();

	const [createPriceList] = useCreatePriceListMutation();

	const [updatePriceList] = useUpdatePriceListMutation();

	useEffect(() => {
		if (!open) form.resetFields();
	}, [open, form]);

	useEffect(() => {
		if (!priceList) return;

		getCategoriesBreedInByFarm({ farmCode: "FV0544" });

		form.setFieldsValue({
			...priceList,
			validFrom: dayjs(priceList.validFrom),
			validTo: dayjs(priceList.validTo),
		});
	}, [priceList, form, getCategoriesBreedInByFarm]);

	const handleOnFinish = async (values: PriceListForm) => {
		const { validFrom, validTo, customerCode, productCode, ...rest } = values;

		try {
			const post = {
				...rest,
				customerCode,
				customerName: dataGetCustomers?.find((item) => item.customerId === customerCode)?.customerName,
				productCode,
				productName: dataBreedInByFarm?.find((item) => item.code === productCode)?.name,
				validFrom: dayjs(validFrom).toISOString(),
				validTo: dayjs(validTo).toISOString(),
				applyAllHouse: false,
			};

			if (!priceList) {
				await createPriceList([post]).unwrap();
			} else {
				await updatePriceList({
					...post,
					id: priceList.id,
				}).unwrap();
			}

			onClose();
			notification.success({
				message: `${!priceList ? "Tạo mới" : "Cập nhập"} đơn hàng thành công !`,
			});
		} catch (error: any) {
			notification.error({
				message: error?.data?.message,
			});
		}
	};

	const validFromWatch = Form.useWatch("validFrom", form);

	return (
		<Drawer
			title={title}
			open={open}
			width={"100%"}
			onClose={onClose}
			footer={
				<Flex gap={10} justify="flex-end">
					<Button loading={isLoadingQueries || isLoadingMutations} disabled={isLoadingQueries || isLoadingMutations} onClick={onClose} icon={<CloseOutlined />}>
						Huỷ bỏ
					</Button>
					<Button loading={isLoadingQueries || isLoadingMutations} disabled={isLoadingQueries || isLoadingMutations} type="primary" onClick={() => form.submit()} ghost icon={<SaveOutlined />}>
						Lưu
					</Button>
				</Flex>
			}>
			<Spin spinning={isLoadingQueries || isLoadingMutations} tip="Đang tải...">
				<Form<PriceListForm> form={form} onFinish={handleOnFinish} layout="vertical">
					<Row gutter={[40, 0]}>
						<Col span={12}>
							<Form.Item<PriceListForm> label="ID Bảng giá" name="priceListCode" initialValue={"PL..."} rules={[{ required: true, message: "Nhập ID Bảng giá" }]}>
								<Input disabled placeholder="Nhập ID Bảng giá" />
							</Form.Item>
						</Col>

						<Col span={12}>
							<Form.Item<PriceListForm> label="Khách hàng" name="customerCode" rules={[{ required: true, message: "Chọn khách hàng" }]}>
								<Select
									placeholder="Chọn khách hàng"
									showSearch
									allowClear
									filterOption={handleSelectFilter}
									options={optionsCustomers?.map((item) => ({ label: `[${item.customerId}]: ${item.customerName}`, value: item.customerId }))}
								/>
							</Form.Item>
						</Col>

						<Col span={12}>
							<Form.Item<PriceListForm> label="Trại" name="farmCode" rules={[{ required: true, message: "Chọn trại" }]}>
								<Select
									placeholder="Chọn trại"
									showSearch
									allowClear
									filterOption={handleSelectFilter}
									onClear={handleOnClearFarms}
									notFoundContent={isFetchingGetFarms ? <Spin size="small" /> : <Empty />}
									onChange={(value) => {
										if (!value) return;

										getCategoriesBreedInByFarm({ farmCode: value });
										form.setFieldValue("productCode", null);
									}}
									options={optionsFarms?.map((item, key: number) => ({ label: `[${item.farm_code}]: ${item.farm_name}`, value: item.farm_code }))}
								/>
							</Form.Item>
						</Col>

						<Col span={12}>
							<Form.Item<PriceListForm> label={"Sản phẩm"} name="productCode" rules={[{ required: true, message: "Chọn sản phẩm" }]}>
								<Select
									style={{ width: "100%" }}
									placeholder="Chọn"
									showSearch
									allowClear
									filterOption={handleSelectFilter}
									options={dataBreedInByFarm?.map((item) => ({
										label: `[${item.code}]: ${item.name}`,
										value: item.code,
									}))}
								/>
							</Form.Item>
						</Col>

						<Col span={12}>
							<Form.Item label={"Giới tính"} name="gender" rules={[{ required: true, message: "Chọn giới tính" }]}>
								<Select style={{ width: "100%" }} options={Object.keys(GenderChicken).map((gender) => ({ label: gender, value: gender }))} allowClear placeholder="Chọn giới tính" />
							</Form.Item>
						</Col>

						<Col span={12}>
							<Form.Item<PriceListForm> label={"Gà loại"} name="chickenType" rules={[{ required: true, message: "Chọn gà loại" }]}>
								<Select
									style={{ width: "100%" }}
									options={Array(4)
										.fill(99)
										.map((_, index) => ({ label: (index + 1).toString(), value: index + 1 }))}
									allowClear
									placeholder="chọn"
								/>
							</Form.Item>
						</Col>

						<Col span={12}>
							<Form.Item<PriceListForm> label={"Đơn vị tính"} name="uom" initialValue={EnumUomPriceList.KG} rules={[{ required: true, message: "Chọn gà loại" }]}>
								<Select style={{ width: "100%" }} options={Object.keys(EnumUomPriceList).map((gender) => ({ label: gender, value: gender }))} allowClear placeholder="Chọn đơn vị tính" />
							</Form.Item>
						</Col>

						<Col span={12}>
							<Form.Item<PriceListForm> label="Giá" name="priceAmount">
								<InputNumber style={{ width: "100%" }} placeholder="Nhập giá" formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")} />
							</Form.Item>
						</Col>

						<Col span={12}>
							<Form.Item<PriceListForm> label={"Tiền tệ"} name="currency" initialValue={EnumCurrencyPriceList.VND} rules={[{ required: true, message: "Chọn tiền tệ" }]}>
								<Select style={{ width: "100%" }} options={Object.keys(EnumCurrencyPriceList).map((gender) => ({ label: gender, value: gender }))} allowClear placeholder="Chọn đơn vị tính" />
							</Form.Item>
						</Col>

						<Col span={12}>
							<Form.Item<PriceListForm> label={"Hiệu lực từ ngày"} name="validFrom" rules={[{ required: true, message: "Hiệu lực từ ngày" }]}>
								<DatePicker
									placeholder="Chọn ngày giờ"
									format={"DD-MM-YYYY HH:mm"}
									showTime={{ format: "HH:mm" }}
									disabledDate={(date) => !date || date.isBefore(dayjs())}
									style={{ width: "100%" }}
									onChange={() => form.setFieldValue("validTo", dayjs("09:00", "HH:mm").add(1, "day"))}
								/>
							</Form.Item>
						</Col>

						<Col span={12}>
							<Form.Item<PriceListForm> label={"Đến ngày"} name="validTo" rules={[{ required: true, message: "Chọn tiền tệ" }]}>
								<DatePicker
									disabled={!validFromWatch}
									placeholder="Chọn ngày giờ"
									format={"DD-MM-YYYY HH:mm"}
									showTime={{ format: "HH:mm" }}
									disabledDate={(date) => !date || date.isBefore(dayjs(validFromWatch))}
									style={{ width: "100%" }}
								/>
							</Form.Item>
						</Col>

						<Col span={12}>
							<Form.Item<PriceListForm> label={"Trạng thái"} name="status" initialValue={EnumStatusPriceList.valid} rules={[{ required: true, message: "Chọn tiền tệ" }]}>
								<Select
									disabled
									style={{ width: "100%" }}
									options={Object.keys(EnumStatusPriceList).map((status: string) => ({ label: translationStatusPriceList(status as keyof typeof EnumStatusPriceList), value: status }))}
									allowClear
									placeholder="Chọn đơn vị tính"
								/>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</Spin>
		</Drawer>
	);
};
